/**
 * Minimal RFC 5545 RRULE builder/parser/describer for habit scheduling.
 *
 * We intentionally do NOT depend on rrule.js — we only need the small
 * subset that mirrors the Google Calendar editor UX:
 *   FREQ, INTERVAL, BYDAY, BYMONTHDAY, BYSETPOS, COUNT, UNTIL.
 *
 * Time-of-day is stored OUTSIDE the RRULE as `scheduled_times_local`
 * (array of "HH:MM" local strings) so we can support multiple times
 * per day cleanly. DTSTART is stored in `dtstart` (YYYY-MM-DD) and
 * also kept out of the RRULE string.
 */

export type Frequency = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'
export type RWeekday = 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU'
export type MonthlyMode = 'day_of_month' | 'nth_weekday'
export type EndMode = 'never' | 'on_date' | 'after_count'

export interface RRuleSpec {
  freq: Frequency
  interval: number
  byweekday?: RWeekday[]         // WEEKLY only
  monthlyMode?: MonthlyMode       // MONTHLY only
  bymonthday?: number             // MONTHLY day_of_month (1..31)
  bysetpos?: number               // MONTHLY nth_weekday (1..5 or -1)
  bydayForMonthly?: RWeekday      // MONTHLY nth_weekday
  endMode: EndMode
  count?: number                  // endMode === 'after_count'
  until?: string                  // endMode === 'on_date', YYYY-MM-DD
}

export const WEEKDAYS: { code: RWeekday; short: string; long: string; idx: number }[] = [
  { code: 'MO', short: 'Mon', long: 'Monday', idx: 1 },
  { code: 'TU', short: 'Tue', long: 'Tuesday', idx: 2 },
  { code: 'WE', short: 'Wed', long: 'Wednesday', idx: 3 },
  { code: 'TH', short: 'Thu', long: 'Thursday', idx: 4 },
  { code: 'FR', short: 'Fri', long: 'Friday', idx: 5 },
  { code: 'SA', short: 'Sat', long: 'Saturday', idx: 6 },
  { code: 'SU', short: 'Sun', long: 'Sunday', idx: 0 },
]

const CODE_TO_LONG: Record<RWeekday, string> = Object.fromEntries(
  WEEKDAYS.map((w) => [w.code, w.long]),
) as Record<RWeekday, string>

const NTH_LABELS: Record<number, string> = {
  1: 'first', 2: 'second', 3: 'third', 4: 'fourth', 5: 'fifth', [-1]: 'last',
}

export function defaultSpec(): RRuleSpec {
  return { freq: 'DAILY', interval: 1, endMode: 'never' }
}

/**
 * Build an RFC-5545 RRULE string (without DTSTART, without BYHOUR/BYMINUTE).
 * Example: "FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,WE,FR"
 */
export function buildRRule(spec: RRuleSpec): string {
  const parts: string[] = [`FREQ=${spec.freq}`]
  if (spec.interval && spec.interval > 1) parts.push(`INTERVAL=${spec.interval}`)

  if (spec.freq === 'WEEKLY' && spec.byweekday && spec.byweekday.length > 0) {
    parts.push(`BYDAY=${spec.byweekday.join(',')}`)
  }

  if (spec.freq === 'MONTHLY') {
    if (spec.monthlyMode === 'day_of_month' && spec.bymonthday) {
      parts.push(`BYMONTHDAY=${spec.bymonthday}`)
    } else if (spec.monthlyMode === 'nth_weekday' && spec.bysetpos && spec.bydayForMonthly) {
      parts.push(`BYDAY=${spec.bydayForMonthly}`)
      parts.push(`BYSETPOS=${spec.bysetpos}`)
    }
  }

  if (spec.endMode === 'after_count' && spec.count && spec.count > 0) {
    parts.push(`COUNT=${spec.count}`)
  } else if (spec.endMode === 'on_date' && spec.until) {
    // UNTIL must be a date-time; we use end-of-day UTC for the picked date.
    const compact = spec.until.replace(/-/g, '') + 'T235959Z'
    parts.push(`UNTIL=${compact}`)
  }

  return parts.join(';')
}

/** Parse an RRULE string (our subset). Returns defaults for missing parts. */
export function parseRRule(input: string | null | undefined): RRuleSpec {
  const spec = defaultSpec()
  if (!input) return spec
  const body = input.startsWith('RRULE:') ? input.slice(6) : input
  const kv: Record<string, string> = {}
  for (const seg of body.split(';')) {
    const [k, v] = seg.split('=')
    if (k && v) kv[k.trim().toUpperCase()] = v.trim()
  }

  if (kv.FREQ === 'DAILY' || kv.FREQ === 'WEEKLY' || kv.FREQ === 'MONTHLY' || kv.FREQ === 'YEARLY') {
    spec.freq = kv.FREQ
  }
  if (kv.INTERVAL) {
    const n = parseInt(kv.INTERVAL, 10)
    if (!Number.isNaN(n) && n > 0) spec.interval = n
  }
  if (kv.BYDAY) {
    const codes = kv.BYDAY.split(',').map((c) => c.trim().toUpperCase()) as RWeekday[]
    const valid = codes.filter((c) => WEEKDAYS.some((w) => w.code === c))
    if (spec.freq === 'WEEKLY') {
      spec.byweekday = valid
    } else if (spec.freq === 'MONTHLY' && valid.length === 1) {
      spec.bydayForMonthly = valid[0]
      spec.monthlyMode = 'nth_weekday'
    }
  }
  if (kv.BYMONTHDAY) {
    const n = parseInt(kv.BYMONTHDAY, 10)
    if (!Number.isNaN(n)) {
      spec.bymonthday = n
      spec.monthlyMode = 'day_of_month'
    }
  }
  if (kv.BYSETPOS) {
    const n = parseInt(kv.BYSETPOS, 10)
    if (!Number.isNaN(n)) spec.bysetpos = n
  }
  if (kv.COUNT) {
    const n = parseInt(kv.COUNT, 10)
    if (!Number.isNaN(n) && n > 0) {
      spec.endMode = 'after_count'
      spec.count = n
    }
  }
  if (kv.UNTIL) {
    // Accept YYYYMMDD or YYYYMMDDTHHMMSSZ
    const raw = kv.UNTIL.slice(0, 8)
    if (raw.length === 8) {
      spec.endMode = 'on_date'
      spec.until = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`
    }
  }

  return spec
}

/** Format a "HH:MM" (24h) as "8:00 AM" / "8:00 PM" */
export function formatTime12h(hhmm: string): string {
  const [hStr, mStr] = hhmm.split(':')
  const h = parseInt(hStr, 10)
  const m = parseInt(mStr, 10)
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm
  const period = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(m).padStart(2, '0')} ${period}`
}

/** Human-readable summary: "Weekly on Mon, Wed, Fri at 8:00 AM, 8:00 PM — until Dec 31, 2025" */
export function describeRRule(spec: RRuleSpec, times: string[]): string {
  const intervalPart = spec.interval > 1 ? `every ${spec.interval} ` : ''
  let base = ''

  if (spec.freq === 'DAILY') {
    base = spec.interval > 1 ? `Every ${spec.interval} days` : 'Daily'
  } else if (spec.freq === 'WEEKLY') {
    const days = (spec.byweekday || []).map((c) => CODE_TO_LONG[c].slice(0, 3))
    if (days.length === 0) {
      base = spec.interval > 1 ? `Every ${spec.interval} weeks` : 'Weekly'
    } else if (days.length === 7) {
      base = spec.interval > 1 ? `Every ${spec.interval} weeks, every day` : 'Daily'
    } else {
      base = `${spec.interval > 1 ? `Every ${spec.interval} weeks on` : 'Weekly on'} ${days.join(', ')}`
    }
  } else if (spec.freq === 'MONTHLY') {
    if (spec.monthlyMode === 'nth_weekday' && spec.bysetpos && spec.bydayForMonthly) {
      base = `${intervalPart}Monthly on the ${NTH_LABELS[spec.bysetpos] || spec.bysetpos} ${CODE_TO_LONG[spec.bydayForMonthly]}`
      base = base.charAt(0).toUpperCase() + base.slice(1)
    } else {
      base = `${intervalPart}Monthly on day ${spec.bymonthday || 1}`
      base = base.charAt(0).toUpperCase() + base.slice(1)
    }
  } else if (spec.freq === 'YEARLY') {
    base = spec.interval > 1 ? `Every ${spec.interval} years` : 'Yearly'
  }

  const timeStr = times.length > 0
    ? ` at ${times.map(formatTime12h).join(', ')}`
    : ''

  let endStr = ''
  if (spec.endMode === 'after_count' && spec.count) {
    endStr = `, for ${spec.count} occurrence${spec.count === 1 ? '' : 's'}`
  } else if (spec.endMode === 'on_date' && spec.until) {
    try {
      const d = new Date(spec.until)
      endStr = ` — until ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
    } catch {
      endStr = ` — until ${spec.until}`
    }
  }

  return `${base}${timeStr}${endStr}`
}

/**
 * Derive the legacy `target_cadence` string ('daily' | 'weekly' |
 * 'biweekly' | 'monthly') from an RRuleSpec, so existing read paths that
 * still display the legacy field keep rendering something sensible.
 */
export function deriveLegacyCadence(spec: RRuleSpec): string {
  if (spec.freq === 'DAILY') return 'daily'
  if (spec.freq === 'WEEKLY') return spec.interval === 2 ? 'biweekly' : 'weekly'
  if (spec.freq === 'MONTHLY') return 'monthly'
  if (spec.freq === 'YEARLY') return 'yearly'
  return 'daily'
}

/** Weekday short names for legacy `preferred_days` field. */
const CODE_TO_LEGACY_DAY: Record<RWeekday, string> = {
  MO: 'monday', TU: 'tuesday', WE: 'wednesday', TH: 'thursday',
  FR: 'friday', SA: 'saturday', SU: 'sunday',
}

export function deriveLegacyPreferredDays(spec: RRuleSpec): string[] {
  if (spec.freq !== 'WEEKLY' || !spec.byweekday) return []
  return spec.byweekday.map((c) => CODE_TO_LEGACY_DAY[c])
}

/**
 * Map a single "HH:MM" to the legacy categorical `preferred_time_window`
 * (morning|afternoon|evening|night) — best-effort so old readers stay
 * meaningful. Uses the same boundaries as the migration script.
 */
export function deriveLegacyTimeWindow(hhmm: string): string {
  const h = parseInt(hhmm.split(':')[0] || '8', 10)
  if (h >= 5 && h < 7) return 'early_morning'
  if (h >= 7 && h < 12) return 'morning'
  if (h >= 12 && h < 17) return 'afternoon'
  if (h >= 17 && h < 21) return 'evening'
  return 'night'
}

/**
 * Back-compat: synthesize an RRuleSpec + default time from legacy habit
 * fields when the habit doesn't yet have an explicit `rrule`.
 */
export function rruleFromLegacy(
  target_cadence: string | undefined,
  preferred_days: string[] | undefined,
  preferred_time_window: string | undefined,
): { spec: RRuleSpec; times: string[] } {
  const spec = defaultSpec()
  const legacy = (target_cadence || 'daily').toLowerCase()
  if (legacy === 'weekly') {
    spec.freq = 'WEEKLY'
    spec.interval = 1
  } else if (legacy === 'biweekly') {
    spec.freq = 'WEEKLY'
    spec.interval = 2
  } else if (legacy === 'monthly') {
    spec.freq = 'MONTHLY'
    spec.monthlyMode = 'day_of_month'
    spec.bymonthday = 1
  } else {
    spec.freq = 'DAILY'
  }

  if ((spec.freq === 'WEEKLY') && preferred_days && preferred_days.length > 0) {
    const legacyToCode: Record<string, RWeekday> = {
      monday: 'MO', mon: 'MO', tuesday: 'TU', tue: 'TU',
      wednesday: 'WE', wed: 'WE', thursday: 'TH', thu: 'TH',
      friday: 'FR', fri: 'FR', saturday: 'SA', sat: 'SA',
      sunday: 'SU', sun: 'SU',
    }
    spec.byweekday = preferred_days
      .map((d) => legacyToCode[d.toLowerCase()])
      .filter((c): c is RWeekday => !!c)
  }

  const windowTimes: Record<string, string> = {
    early_morning: '05:00', morning: '08:00', afternoon: '13:00', evening: '18:00', night: '21:00',
  }
  const time = windowTimes[(preferred_time_window || 'morning').toLowerCase()] || '08:00'
  return { spec, times: [time] }
}
